import { getDocFromServer, getDocsFromServer, onSnapshot } from 'firebase/firestore';
import { reportCaughtError } from './clientErrorReporting';
import { isRecoverableFirestoreAssertion } from './firestoreAssertionRecovery';

function getListenerSource(context = {}) {
  const label = typeof context.label === 'string' ? context.label.trim() : '';
  return label ? `firestore.listener.${label}`.slice(0, 80) : 'firestore.listener';
}

export function isEmptyCacheSnapshot(snapshot) {
  if (snapshot?.metadata?.fromCache !== true) return false;
  if (snapshot?.empty === true) return true;
  if (typeof snapshot?.exists === 'function' && snapshot.exists() === false) return true;
  return Array.isArray(snapshot?.docs) && snapshot.docs.length === 0;
}

export function isRecoverableFirestoreListenTargetError(error) {
  const code = String(error?.code || '').toLowerCase();
  const message = String(error?.message || error || '');
  return code === 'already-exists' && /^Target ID already exists: \d+$/i.test(message);
}

function reportListenerError(error, context = {}) {
  if (isRecoverableFirestoreAssertion(error)) return;
  if (isRecoverableFirestoreListenTargetError(error)) return;
  const code = String(error?.code || '').trim().toLowerCase();
  if (
    context.suppressPermissionDenied === true
    && (code === 'permission-denied' || code === 'firestore/permission-denied')
  ) return;

  const { suppressPermissionDenied, ...reportContext } = context;
  delete reportContext.includeMetadataChanges;
  delete reportContext.ignoreEmptyCacheSnapshot;

  try {
    const report = reportCaughtError(error, {
      source: getListenerSource(context),
      severity: 'error',
      context: reportContext,
    });
    report?.catch?.(() => {});
  } catch {
    // Error reporting must never break the listener or its recovery path.
  }
}

export function safeOnSnapshot(target, onNext, onError, context = {}) {
  const ignoreEmptyCacheSnapshot = context.ignoreEmptyCacheSnapshot !== false;
  let authoritativeReadStarted = false;
  const safeNext = typeof onNext === 'function'
    ? (...args) => {
      if (ignoreEmptyCacheSnapshot && isEmptyCacheSnapshot(args[0])) {
        if (!authoritativeReadStarted) {
          authoritativeReadStarted = true;
          const readFromServer = Array.isArray(args[0]?.docs) ? getDocsFromServer : getDocFromServer;
          if (typeof readFromServer === 'function') {
            Promise.resolve(readFromServer(target)).then((serverSnapshot) => {
              safeNext(serverSnapshot);
            }).catch((err) => safeError(err));
          }
        }
        return;
      }
      try {
        onNext(...args);
      } catch (err) {
        reportListenerError(err, { ...context, phase: 'next' });
        throw err;
      }
    }
    : onNext;

  const safeError = (err) => {
    reportListenerError(err, { ...context, phase: 'error' });
    if (typeof onError !== 'function') return;

    try {
      onError(err);
    } catch (handlerError) {
      reportListenerError(handlerError, { ...context, phase: 'error-handler' });
    }
  };

  let unsubscribe = () => {};
  try {
    unsubscribe = context.includeMetadataChanges === true
      ? onSnapshot(target, { includeMetadataChanges: true }, safeNext, safeError)
      : onSnapshot(target, safeNext, safeError);
  } catch (err) {
    safeError(err);
    return () => {};
  }

  let didUnsubscribe = false;
  return () => {
    if (didUnsubscribe) return;
    didUnsubscribe = true;

    try {
      unsubscribe();
    } catch (err) {
      reportListenerError(err, { ...context, phase: 'unsubscribe' });
    }
  };
}
